// @ts-check
// apps/crm/dashboard/modules/jarvis/marks.js
// ── A stopwatch nobody sees unless they ask for it ────────────────────
// (Brief S2 lever 2, Ruling J120, 2026-09-16)
//
// ⚠️ THE ONE THING A TEST SUITE CANNOT MEASURE IS THE THING THIS ROUND IS
// ABOUT. jsdom has no Web Audio API, no real socket and no real network, so
// "⌘J to Listening… is now a quarter of a second" is only ever provable with a
// stopwatch in a real browser on localhost. Rather than guess at it afterwards
// from console.time() sprinkled in by hand, the code carries its own marks and
// they are switched off unless somebody deliberately turns them on:
//
//     localStorage.atlasDebug = '1'      // then reload, press ⌘J, speak
//     window.__atlasMarks                // the readings, oldest first
//
// ⚠️ OFF FOR EVERY COACH, ALWAYS. Nothing here runs without that flag: no
// array is created, no timestamp is read, nothing is kept. A coach's browser
// must not quietly accumulate a timing log of everything they said.
//
// ⚠️ AND A MARK NEVER CARRIES THE TOKEN, or a transcript, or anything else a
// screenshot of a console would be the wrong place for. Names, milliseconds
// and booleans only — the same rule mic.js and stt-token.js keep about
// console lines, in a different hat.

/** @typedef {{ name: string, t: number, at: number, sinceOpen: number }} AtlasMark */

/** How many readings are kept before the oldest fall off the front. */
const MAX_MARKS = 300;

/** Whether the coach (or whoever is measuring) asked for this. */
function on() {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem('atlasDebug') === '1';
  } catch {
    // A private window, a browser with site data blocked, an iframe with no
    // storage access: not measured, and certainly not an exception thrown out
    // of a keydown handler.
    return false;
  }
}

/**
 * Record one reading, if anyone is measuring.
 *
 * ⚠️ IT NEVER THROWS. Every call site is on a path whose whole job is to be
 * fast and silent — a keydown listener, a socket callback, a fetch's tail.
 *
 * @param {string} name what just happened: 'open', 'audio-warm', 'token',
 *   'socket', 'listening', 'turn-eager', 'turn-end', and — since brief R1
 *   (Ruling J125) — 'eager_end' (the instant the BAR acts on Cartesia's early
 *   guess, the zero of this round's own measurement), 'ack:first-sound' and
 *   'question:first-sound' (the two sentences Atlas composes for itself),
 *   'card', 'speak:start', 'speak:answered', 'speak:first-sound', 'speak:end',
 *   'on-it:start'. Brief R3/R4 adds 'bridge:first-sound' (the coach's yes to
 *   Atlas's first sound over the write) and 'follow-up:listening' (the bar
 *   listening again with no key pressed, after a finished exchange). Rhythm fix
 *   1 adds 'submit:start' — the first line of index.js's submit(), which splits
 *   the old eager_end → on-it:start gap into the part spent getting to the
 *   submission and the part that is "On it."'s own 300 ms patience. Brief W2
 *   (Ruling J148) adds the wake word's four: 'wake:armed' (the engine is loaded
 *   and the room is being listened to, carrying its own `loadMs`), 'wake:heard'
 *   (the spotter fired and the bar is opening), 'wake:accepted' and
 *   'wake:rejected' (what Cartesia's own transcript then said about it). The
 *   reading that matters is `since('wake:heard', 'listening')` — the gap between
 *   a coach saying their own name for Atlas and the bar really being able to
 *   hear them, which is the only part of the wake path that is ours to shorten.
 * @param {Record<string, string|number|boolean>} [detail] anything else worth
 *   reading later — never a token, never a transcript.
 */
export function mark(name, detail) {
  try {
    if (!on()) return;
    const w = /** @type {any} */ (window);
    const marks = w.__atlasMarks || (w.__atlasMarks = []);
    const t = Math.round(performance.now() * 10) / 10;
    // ⚠️ THE NUMBER THE OWNER ASKED FOR IS ⌘J → "Listening…". Two page clocks
    // he has to subtract by hand in a console is not that number, so every
    // reading carries its own distance from the last time the bar opened.
    let opened = 0;
    for (let i = marks.length - 1; i >= 0; i--) {
      if (marks[i].name === 'open') { opened = marks[i].t; break; }
    }
    // `t` and `sinceOpen` are what S2's callers read; `at` (the same clock) is
    // what S3's since() below reads — one helper, both rounds' marks.
    marks.push({ name, t, at: t, sinceOpen: Math.round((t - opened) * 10) / 10, ...(detail || {}) });
    if (marks.length > MAX_MARKS) marks.splice(0, marks.length - MAX_MARKS);
  } catch { /* a stopwatch is never worth an exception */ }
}

/**
 * Every reading so far, oldest first — what `window.__atlasMarks` holds.
 * @returns {AtlasMark[]}
 */
export function readMarks() {
  try {
    return /** @type {any} */ (window).__atlasMarks || [];
  } catch {
    return [];
  }
}

/**
 * Milliseconds between the LAST mark named `from` and the LAST mark named
 * `to`, or null when either is missing — the read the real-browser pass makes
 * (`marks.since('card', 'speak:first-sound')`, speed-s3-report.md §5).
 * @param {string} from
 * @param {string} to
 * @returns {number|null}
 */
export function since(from, to) {
  const list = readMarks();
  const last = (n) => { for (let i = list.length - 1; i >= 0; i--) if (list[i].name === n) return list[i]; return null; };
  const a = last(from);
  const b = last(to);
  return a && b ? b.at - a.at : null;
}
